import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Trash2, Lock, CheckCircle2 } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { money, Row } from "./checkoutUtils";

export const OrderSummary = ({
  items,
  removeItem,
  dueToday,
  monthly,
  finalDueToday,
  discount,
  form,
  setForm,
  applyPromo,
  submitting,
}) => (
  <div className="lg:col-span-2">
    <div className="sticky top-24 bg-white rounded-3xl border border-slate-200 shadow-md overflow-hidden">
      <div className="knight-gradient text-white p-5 flex items-center gap-3">
        <ShoppingCart size={20} className="text-knight-gold" />
        <h3 className="font-display text-lg font-bold">Order Summary</h3>
      </div>

      <div className="p-5">
        {items.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-knight-navy/60 mb-2">No items in cart</p>
            <Link to="/pricing" className="text-sm font-semibold text-knight-navy underline">Browse plans</Link>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map(it => (
              <li key={it.id} className="py-3 flex items-start gap-3">
                <div className="flex-1">
                  <p className="font-bold text-sm text-knight-navy">{it.name}</p>
                  {it.description && <p className="text-[11px] text-knight-navy/55 mt-0.5">{it.description}</p>}
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs">
                    {it.setupFee > 0 && <span className="text-knight-navy/70">First Work Fee: <span className="font-bold">{money(it.setupFee)}</span></span>}
                    {it.monthly > 0 && <span className="text-knight-navy/70">Monthly: <span className="font-bold">{money(it.monthly)}</span></span>}
                    {it.oneTime > 0 && <span className="text-knight-navy/70">One-time: <span className="font-bold">{money(it.oneTime)}</span></span>}
                  </div>
                </div>
                <button type="button" onClick={() => removeItem(it.id)} aria-label="Remove" data-testid={`remove-item-${it.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
              </li>
            ))}
          </ul>
        )}

        {items.length > 0 && (
          <>
            <div className="border-t border-slate-200 mt-3 pt-4 space-y-2 text-sm">
              <Row label="Due Today (one-time)" value={money(dueToday)} />
              <Row label="Recurring Monthly" value={`${money(monthly)}/mo`} />
              {form.promoApplied && (
                <Row label={`Promo (${form.promoApplied.code})`} value={`- ${money(discount)}`} />
              )}
            </div>

            {/* Promo code */}
            <div className="mt-4 rounded-xl border border-dashed border-knight-navy/20 bg-slate-50/60 p-3">
              <label className="text-[11px] font-bold tracking-widest text-knight-navy/60 uppercase mb-2 block">Promo code</label>
              <div className="flex gap-2">
                <Input
                  value={form.promoCode}
                  onChange={(e) => setForm(f => ({ ...f, promoCode: e.target.value.toUpperCase() }))}
                  placeholder="ENTER CODE"
                  className="font-mono text-sm tracking-wider uppercase"
                  data-testid="promo-code-input"
                />
                <Button type="button" onClick={applyPromo} className="bg-knight-navy hover:bg-knight-navy-dark text-white font-bold whitespace-nowrap" data-testid="apply-promo-btn">
                  Apply
                </Button>
              </div>
              {form.promoApplied && (
                <p className="text-[11px] text-emerald-700 font-semibold mt-2 flex items-center gap-1">
                  <CheckCircle2 size={12} /> {form.promoApplied.label}
                </p>
              )}
            </div>

            {/* Biweekly opt-in */}
            <label className="mt-4 flex items-start gap-3 cursor-pointer bg-knight-gold/10 border border-knight-gold/40 rounded-xl p-3">
              <Checkbox checked={form.biweekly} onCheckedChange={(v) => setForm(f => ({ ...f, biweekly: Boolean(v) }))} className="mt-0.5" />
              <div>
                <p className="text-sm font-bold text-knight-navy">I would like Biweekly Monthly Payments</p>
                <p className="text-[11px] text-knight-navy/65 mt-0.5">Split your monthly fee into two charges aligned with your paychecks ({monthly > 0 ? money(monthly / 2) : "$0"} every 2 weeks).</p>
              </div>
            </label>

            <div className="mt-4 pt-4 border-t border-slate-200 flex items-baseline justify-between">
              <span className="font-semibold text-knight-navy">Total Due Now</span>
              <span className="font-display text-3xl font-extrabold text-knight-navy" data-testid="checkout-total">{money(finalDueToday)}</span>
            </div>
          </>
        )}

        <Button type="submit" size="lg" disabled={submitting || items.length === 0} data-testid="complete-order-btn" className="w-full mt-6 bg-knight-gold hover:bg-knight-gold/90 text-knight-navy font-bold disabled:opacity-50 btn-lift">
          <Lock size={16} className="mr-2" />
          {submitting ? "Processing…" : "Complete Secure Order"}
        </Button>

        <p className="text-[10px] text-knight-navy/45 text-center mt-3">Your information is encrypted and never sold to third parties.</p>
      </div>
    </div>
  </div>
);
